import { AspectRatio, Box, Flex, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver, Elements } from '@kontent-ai/delivery-sdk';
import { ContentPage, ContentPageActor, NavigationCategory, NavigationLanguageItem } from 'models';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { normalize } from 'pages/api/sitemap';
import React, { FC, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import slugify from 'slugify';
import { withTranslations } from 'store/translations';
import { internalPages } from 'utils/getInternalPage';
import { handleLanguageChange } from 'utils/handleLocaleChange';
import { imageFormatter } from 'utils/imageFormatter';
import { hrefLink } from 'utils/normalization/hrefLink';

import { LinkType } from '../../lib/enums/LinkType';
import Breadcrumb from '../base/Breadcrumb';
import { Link as BaseLink } from '../base/Link';
import { MegaMenuActiveTabProps } from './NavBar';

interface HighlightedItemProps {
  image: ReturnType<typeof imageFormatter>;
  headline: string;
  description: string;
  url: string;
  linkText: string;
}

interface MegaMenuProps {
  megaMenuProps: MegaMenuActiveTabProps;
  setMegaMenuIndexMobile: React.Dispatch<React.SetStateAction<number>>;
  setIsMegaMenuActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export const MegaMenu: FC<MegaMenuProps> = ({ megaMenuProps, setMegaMenuIndexMobile, setIsMegaMenuActive }) => {
  const translations = withTranslations();
  const router = useRouter();
  const [cookie, setCookie] = useCookies(['NEXT_LOCALE']);
  const [_initialLocaleCookie, _setInitialLocaleCookie, removeInitialLocaleCookie] = useCookies(['INITIAL_LOCALE']);
  const [highlightedItem, setHighlightedItem] = useState<HighlightedItemProps>();

  const [subpages, setSubpages] = useState(megaMenuProps.subpages);
  const [category, setCategory] = useState({
    title: megaMenuProps.categoryTitle,
    description: megaMenuProps.categoryDescription,
  });
  const [activeExploreBy, setActiveExploreBy] = useState<string>();
  const [exploreByPages, setExploreByPages] = useState<ContentPage[]>();
  const [exploreByOverviewPage, setExploreByOverviewPage] = useState('');
  const [linkToOverviewPage, setLinkToOverviewPage] = useState('');
  const [isChangingViews, setIsChangingViews] = useState(false);
  const [isChangingViewsExploreBy, setIsChangingViewsExploreBy] = useState(false);
  const CTAText = megaMenuProps.CTAText;
  const KontentAI = megaMenuProps.KontentAI;
  const CTAOne = megaMenuProps.CTAOne;
  const KontentAIOne = megaMenuProps.LinktoKontentItem;
  const LinkUrl =
    KontentAIOne !== undefined
      ? normalize(KontentAIOne as ContentPage, undefined, undefined, false, translations)?.[
          KontentAIOne.system.codename
        ]
      : '/';

  useEffect(() => {
    if (megaMenuProps.categoryTitle !== category.title) setIsChangingViews(true);
    // -- Timeout is for fade-in/out animation, old text is visible for 0.2s --
    setTimeout(
      () => {
        // -- Reset the explore by pages & third column --
        setLinkToOverviewPage(megaMenuProps.linkToOverview);
        setActiveExploreBy(undefined);
        setExploreByPages([]);
        subpages !== undefined && subpages[0].system.type !== 'navigation_category' && setExploreByOverviewPage('');

        // -- Fetch the highlighted article data (images, text, etc) --
        if (megaMenuProps.highlightedArticle !== null) {
          const HLA = megaMenuProps.highlightedArticle as ContentPage | ContentPageActor;
          // TODO: (Noah) See if there is a way to check if there is a navigation description or not
          // Currently it's never empty...\
          // HLA !== undefined ??

          setHighlightedItem({
            image: imageFormatter(HLA.elements.summaryImageFrontify?.value),
            headline: HLA.elements.summaryTitle.value,
            description: createRichTextHtmlResolver(nodeParser).resolveRichText({
              element: HLA.elements.summaryDescription as Elements.RichTextElement,
            }).html,
            url:
              HLA.system.type === 'content_page'
                ? normalize(HLA as ContentPage, undefined, undefined, false, translations)?.[HLA.system.codename] ?? '/'
                : internalPages(translations, (HLA as ContentPageActor).elements.internalCtaLink.value[0]?.codename) ??
                  (HLA as ContentPageActor).elements.externalCtaLink.value,
            linkText:
              HLA.system.type === 'content_page'
                ? translations.common('readMore')
                : (HLA as ContentPageActor).elements.ctaText.value,
          });
        } else {
          // -- Case there is no highlighted article, the elements wont be rendered at all --
          setHighlightedItem(undefined);
        }
        // -- UseStates for updating elements when clicking on a new category --
        setSubpages(megaMenuProps.subpages);
        setCategory({ title: megaMenuProps.categoryTitle, description: megaMenuProps.categoryDescription });
        setIsChangingViews(false);
      },
      // -- Not on mobile tho --
      window.innerWidth >= 1024 ? 200 : 0,
    );
  }, [megaMenuProps]);
  return (
    <Flex
      w={{ base: '200vw', lg: '100%' }}
      h='100%'
      bg='pebble.40'
      p={{ base: '0', lg: 's' }}
      opacity={{ base: '1', lg: !isChangingViews ? '1' : '0' }}
      transition='opacity 0.3s'
      id='RambollMegaMenu'
    >
      {/* ------------ First column ------------ */}
      <Flex
        direction='column'
        display={{ base: 'none', lg: 'flex' }}
        w={{ base: '100vw', lg: '45%' }}
        // h='100%'
        // TODO: (Noah) Figure out how to handle the overflow
        // overflowY='scroll'
        overflow='hidden'
        pr='s'
        borderRight='1px solid'
        borderColor='mountain.40'
        overflowY='auto'
        sx={{
          '::-webkit-scrollbar': {
            width: '4px',
          },
          '::-webkit-scrollbar-track': {
            width: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'mountain.20',
            borderRadius: '0',
          },

          scrollbarColor: 'var(--chakra-colors-mountain-20) white',
          scrollbarWidth: 'thin',
        }}
      >
        <Box>
          <Text pb='xs' textStyle={{ base: 'mobile.h4', lg: 'desktop.h4' }}>
            {category.title}
          </Text>
          <Box
            pb='xs'
            textStyle={{ base: 'mobile.body.s', lg: 'desktop.body.s' }}
            dangerouslySetInnerHTML={{ __html: category.description }}
          ></Box>
          {KontentAIOne !== undefined && (
            <Box pb='xs'>
              <BaseLink
                locale={router.locale}
                href={LinkUrl ?? '/'}
                colorScheme='cyan.web'
                type={LinkType.SecondaryButton}
                iconName='chevronRightForLink'
                onClick={() => setIsMegaMenuActive(false)}
              >
                {/* TODO: This needs to come from Kontent.ai & have HREF */}
                {CTAOne}
              </BaseLink>{' '}
            </Box>
          )}
        </Box>
        {highlightedItem && (
          <Box as='article'>
            {highlightedItem?.image && (
              <AspectRatio ratio={16 / 9}>
                <Image
                  style={{ paddingTop: '20px' }}
                  src={highlightedItem.image.src}
                  alt={highlightedItem.image.alt}
                  width={highlightedItem.image.width}
                  height={highlightedItem.image.height}
                />
              </AspectRatio>
            )}
            {highlightedItem?.headline !== '' && (
              <Text pt='xs' textStyle={'desktop.h5'}>
                {highlightedItem.headline}
              </Text>
            )}
            {highlightedItem?.description !== '' && (
              <>
                <Box
                  textStyle={'desktop.body.l'}
                  pt='xs'
                  pb='xs'
                  dangerouslySetInnerHTML={{ __html: highlightedItem.description }}
                ></Box>
                <BaseLink
                  locale={router.locale}
                  href={highlightedItem.url ?? '/'}
                  colorScheme='cyan.web'
                  type={LinkType.SecondaryButton}
                  iconName='chevronRightForLink'
                  onClick={() => setIsMegaMenuActive(false)}
                >
                  {/* TODO: This needs to come from Kontent.ai & have HREF */}
                  {highlightedItem.linkText}
                </BaseLink>
              </>
            )}
          </Box>
        )}
      </Flex>
      {/* ------------ First column END ------------ */}

      {/* ------------ Second column ------------ */}
      <Box
        w={{
          base: '100vw',
          lg: subpages !== undefined && subpages[0].system.type === 'navigation_category' ? '33%' : '66%',
        }}
        h='100%'
        borderRight={{
          base: 'none',
          lg: subpages !== undefined && subpages[0].system.type === 'navigation_category' ? '1px solid' : undefined,
        }}
        borderColor={{
          base: 'none',
          lg: subpages !== undefined && subpages[0].system.type === 'navigation_category' ? 'mountain.40' : undefined,
        }}
        pb={{ base: 'l', lg: 'unset' }}
        pl={{ base: 'xs', lg: 's' }}
        pr={{ base: 'xs', lg: '0' }}
        overflowY='auto'
        sx={{
          '::-webkit-scrollbar': {
            width: '4px',
          },
          '::-webkit-scrollbar-track': {
            width: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'mountain.20',
            borderRadius: '0',
          },

          scrollbarColor: 'var(--chakra-colors-mountain-20) white',
          scrollbarWidth: 'thin',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        <Box
          pb='xs'
          borderBottom={'1px solid'}
          borderColor='mountain.40'
          display={{ base: 'block', lg: 'none' }}
          pt='s'
        >
          <Breadcrumb
            name={megaMenuProps.categoryTitle}
            onClick={() => {
              setMegaMenuIndexMobile(0);
            }}
          />
        </Box>
        <UnorderedList listStyleType={'none'} margin='0' pt={{ base: 's', lg: 'unset' }}>
          {subpages !== undefined && subpages[0].system.type === 'navigation_category' && (
            <Text as='h3' pb='xs' display={{ base: 'none', lg: 'block' }}>
              {translations.common('exploreBy')}:
            </Text>
          )}
          {subpages !== undefined &&
            subpages.map((subpage: any, index: number) => {
              const slug = router.query.slug || router.query.mainSubpage || router.query.subcategory;
              const url =
                subpage.system.type === 'content_page'
                  ? hrefLink(subpage, slug, true, translations.locale, translations)
                  : subpage.system.type === 'navigation_language_item'
                  ? `${(subpage as NavigationLanguageItem).elements.languageCodename.value}`
                  : subpage.system.type === 'content_page_actor'
                  ? internalPages(
                      translations,
                      (subpage as ContentPageActor).elements.internalCtaLink.value[0]?.codename,
                    ) ?? (subpage as ContentPageActor).elements.externalCtaLink.value
                  : '/';
              return (
                <ListItem
                  key={index}
                  // -- Gives different colors to the "Explore by" categories when you press them --
                  color={{
                    base: 'unset',
                    lg:
                      subpage.system.type === 'navigation_category' && activeExploreBy === subpage.elements.title.value
                        ? 'cyan.web'
                        : 'unset',
                  }}
                  pb='s'
                >
                  {subpage.system.type === 'navigation_category' ? (
                    // -- If there are "Explore by" elements present --
                    <Flex
                      alignItems='center'
                      justifyContent={'space-between'}
                      gap='s'
                      sx={{
                        _after: {
                          content: '""',
                          borderBottom: '1px solid',
                          borderColor: 'cyan.web',
                          w: '100%',
                          display: {
                            base: 'none',
                            lg: activeExploreBy === subpage.elements.title.value ? 'block' : 'none',
                          },
                        },
                      }}
                    >
                      <Box
                        as='button'
                        _hover={{ color: 'cyan.web' }}
                        textStyle={{ base: 'mobile.body.s', lg: 'desktop.body.s' }}
                        onClick={() => {
                          const timeout = window.innerWidth >= 1024 ? 200 : 0;
                          setActiveExploreBy((subpage as NavigationCategory).elements.title.value);
                          // -- Set explore by pages & animation --
                          if (
                            (subpage as NavigationCategory).elements.linkedPages.linkedItems &&
                            exploreByPages !== (subpage as NavigationCategory).elements.linkedPages.linkedItems
                          ) {
                            setIsChangingViewsExploreBy(true);
                            setTimeout(() => {
                              setExploreByPages((subpage as NavigationCategory).elements.linkedPages.linkedItems);
                              (subpage as NavigationCategory).elements.linkToOverviewPage.value[0]
                                ? setExploreByOverviewPage(
                                    (subpage as NavigationCategory).elements.linkToOverviewPage.value[0].codename,
                                  )
                                : setExploreByOverviewPage('');
                              setIsChangingViewsExploreBy(false);
                            }, timeout);
                          } else if (!(subpage as NavigationCategory).elements.linkedPages.linkedItems) {
                            setTimeout(() => {
                              setIsChangingViewsExploreBy(true);
                              setExploreByOverviewPage('');
                              setExploreByPages([]);
                              setIsChangingViewsExploreBy(false);
                            });
                          }
                          setMegaMenuIndexMobile(2);
                        }}
                      >
                        {(subpage as NavigationCategory).elements.title.value}
                      </Box>
                    </Flex>
                  ) : (
                    <Box pr='s'>
                      {/* TODO: (Ante) Look into overview pages locale change to change filters as well from url => should be done in variants */}
                      {subpage.system.type === 'navigation_language_item' ? (
                        <BaseLink
                          iconName='chevronRight'
                          type={LinkType.Menu}
                          href={'/[...slug].tsx'}
                          positionLock={true}
                          as={megaMenuProps.variants?.[url] || `/${url}`}
                          locale={url}
                          onClick={() => {
                            handleLanguageChange(url, cookie, setCookie);
                            removeInitialLocaleCookie('INITIAL_LOCALE');
                            setIsMegaMenuActive(false);
                          }}
                        >
                          {(subpage as NavigationLanguageItem).elements.title.value}
                        </BaseLink>
                      ) : (
                        <BaseLink
                          locale={router.locale}
                          href={url ?? '/'}
                          positionLock={true}
                          type={LinkType.Menu}
                          iconName='chevronRight'
                          navLinkMode={true}
                          onClick={() => setIsMegaMenuActive(false)}
                        >
                          {/* TODO: Add a link to website ver of selected language */}
                          {subpage.elements.summaryTitle.value}
                        </BaseLink>
                      )}
                    </Box>
                  )}
                </ListItem>
              );
            })}
          {/* ---- Link to overview pages ---- */}
          {!!linkToOverviewPage && (
            <ListItem textStyle={{ base: 'mobile.body.l', lg: 'desktop.body.l' }} pb='s'>
              {/* TODO: ANTE, UPDATE THE KONTENT.AI MODEL TO BE SINGULAR CODENAME AFTER MERGE */}
              {linkToOverviewPage !== 'content_page' && (
                <BaseLink
                  href={'/' + slugify(translations.multiple(linkToOverviewPage), { lower: true })}
                  positionLock={true}
                  locale={router.locale}
                  type={LinkType.SecondaryButton}
                  colorScheme='cyan.web'
                  iconName='chevronRightForLink'
                  onClick={() => setIsMegaMenuActive(false)}
                >
                  {/* TODO: Add a link to website ver of selected language */}
                  {linkToOverviewPage === 'career'
                    ? translations.common('openPositions')
                    : translations.common('seeOurLatestOverview', {
                        overviewTypeMultiple: translations.multiple(linkToOverviewPage),
                      })}
                </BaseLink>
              )}
              {linkToOverviewPage === 'content_page' && (
                <BaseLink
                  href={
                    '/' +
                    slugify(translations.multiple(KontentAI?.replaceAll('_', ' ')), { replacement: '-', lower: true })
                  }
                  positionLock={true}
                  locale={router.locale}
                  type={LinkType.SecondaryButton}
                  colorScheme='cyan.web'
                  iconName='chevronRightForLink'
                  onClick={() => setIsMegaMenuActive(false)}
                >
                  {/* TODO: Add a link to website ver of selected language */}
                  {CTAText}
                </BaseLink>
              )}
            </ListItem>
          )}
          {/* ---- Link to overview pages END ---- */}
        </UnorderedList>
      </Box>
      {/* ------------ Second column END ------------ */}

      {/* ------------ Third column ------------ */}
      <Box
        w={{
          base: '100vw',
          lg: subpages !== undefined && subpages[0].system.type === 'navigation_category' ? '37%' : '0',
        }}
        h='100%'
        pl='30px'
        pb={{ base: 'l', lg: 'unset' }}
        pr={{ base: 'xs', lg: '0' }}
        overflowY='auto'
        sx={{
          '::-webkit-scrollbar': {
            width: '4px',
          },
          '::-webkit-scrollbar-track': {
            width: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'mountain.20',
            borderRadius: '0',
          },

          scrollbarColor: 'var(--chakra-colors-mountain-20) white',
          scrollbarWidth: 'thin',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        <Box
          pb='xs'
          borderBottom={'1px solid'}
          borderColor='mountain.40'
          display={{ base: 'block', lg: 'none' }}
          pt='s'
        >
          <Breadcrumb
            name={activeExploreBy && activeExploreBy}
            onClick={() => {
              setMegaMenuIndexMobile(1);
            }}
          />
        </Box>
        <UnorderedList
          listStyleType={'none'}
          margin='0'
          pt={{ base: 's', lg: 'unset' }}
          opacity={{ base: '1', lg: !isChangingViewsExploreBy ? '1' : '0' }}
          transition='opacity 0.3s'
        >
          {exploreByPages?.[0] &&
            exploreByPages.map((page: ContentPage, index) => {
              return (
                <ListItem key={index} textStyle='desktop.body.book.l' pb='s'>
                  <BaseLink
                    locale={router.locale}
                    href={normalize(page, undefined, undefined, false, translations)?.[page.system.codename] ?? '/'}
                    type={LinkType.Menu}
                    iconName='chevronRight'
                    onClick={() => setIsMegaMenuActive(false)}
                  >
                    {/* TODO: Add a link to website ver of selected language */}
                    {page.elements.summaryTitle.value}
                  </BaseLink>
                </ListItem>
              );
            })}
          {/* ---- Link to overview pages ---- */}
          {exploreByOverviewPage !== '' && exploreByOverviewPage && (
            <ListItem textStyle='desktop.body.book.l' pb='s'>
              <BaseLink
                href={'/' + translations.multiple(exploreByOverviewPage)}
                locale={router.locale}
                type={LinkType.SecondaryButton}
                colorScheme='cyan.web'
                iconName='chevronRightForLink'
                onClick={() => setIsMegaMenuActive(false)}
              >
                {/* TODO: Add a link to website ver of selected language */}
                {translations.common('exploreMoreOverviewTypeMultiple', {
                  overviewTypeMultiple: translations.multiple(exploreByOverviewPage),
                })}
              </BaseLink>
            </ListItem>
          )}
          {/* ---- Link to overview pages END ---- */}
        </UnorderedList>
      </Box>
      {/* ------------ Third column END ------------ */}
    </Flex>
  );
};
